import type { CaptionWord } from '../captions/types';
import { normalizeRanges, range, type TimeRange } from '../time/ranges';
import { toDb, type AudioEnvelope } from './envelope';
import type { LevelStats, SilencePresetId } from './silence';
import type { VoiceTrack } from './voice';

/**
 * Hesitation sounds ("ııı", "eee", "um", "uh") are one vowel held on a steady
 * pitch, with nothing changing for a while. Words move from sound to sound:
 * their pitch and colour keep changing every few hundredths of a second.
 */
export interface FillerParams {
  /** Held sounds shorter than this are normal speech. */
  readonly minSec: number;
  /** Longer steady sounds are music, humming or an engine, not a hesitation. */
  readonly maxSec: number;
  /** Largest pitch step between neighbouring windows (natural log ratio). */
  readonly maxPitchStep: number;
  /** Largest pitch range across the whole sound (natural log ratio). */
  readonly maxPitchSpan: number;
  /** Largest change of the sound's colour between windows. */
  readonly maxChange: number;
}

export const FILLER_PRESETS: Readonly<Record<SilencePresetId, FillerParams>> = {
  gentle: { minSec: 0.55, maxSec: 2.5, maxPitchStep: 0.05, maxPitchSpan: 0.25, maxChange: 0.08 },
  balanced: { minSec: 0.45, maxSec: 2.5, maxPitchStep: 0.06, maxPitchSpan: 0.3, maxChange: 0.1 },
  aggressive: { minSec: 0.4, maxSec: 2.5, maxPitchStep: 0.07, maxPitchSpan: 0.35, maxChange: 0.12 },
};

/** How far a held sound may be widened at each side to take its onset and fade. */
const GROW_SEC = 0.3;
/** …while the pitch stays within this share of the core's range. */
const GROW_PITCH = 0.08;
/** Shorter cuts are not worth the jump they cause. */
const MIN_CUT_SEC = 0.25;
/** A held sound right after a word (“veeee”) keeps its start so the word stays whole. */
const WORD_HEAD_SEC = 0.12;
/** Left at the end of a cut, so the next sound doesn't start with a click. */
const TAIL_SEC = 0.03;
/** If held sounds fill more of the voiced time than this, it is singing or music: cut nothing. */
const MAX_FILLER_SHARE = 0.4;
/** …judged only when there is enough voice to tell. */
const MIN_VOICED_SEC_FOR_SHARE = 8;

/**
 * Hesitations as speech recognisers sometimes write them down (Turkish and
 * English). Deliberately narrow: “e”, “ah” and “hı” carry meaning in Turkish.
 */
const FILLER_WORD = /^(ı+|i{2,}|e{2,}h*|eh|e+hm+|e{2,}m+|ı+m+|h?m{2,}|um+|uh+|uhm+|erm+|ö+h*m*)$/u;

export const isFillerWord = (text: string): boolean => FILLER_WORD.test(text.toLocaleLowerCase('tr').replace(/[^\p{L}]/gu, ''));

export interface FillerDetection {
  /** Ranges that will be removed. */
  readonly fillers: TimeRange[];
}

/**
 * Finds hesitation sounds from the voice analysis (and, when there is a
 * transcript, from filler words in it). Returns nothing for audio that looks
 * like singing or music, and nothing without usable levels — it is always
 * safer to cut nothing than to cut words.
 */
export function detectFillers(
  voice: VoiceTrack,
  envelope: AudioEnvelope,
  levels: LevelStats | null,
  params: FillerParams,
  words: readonly CaptionWord[] = [],
): FillerDetection {
  const fromWords = words.filter((word) => isFillerWord(word.text)).map((word) => range(word.start, word.end));
  if (!levels) return { fillers: normalizeRanges(fromWords) };

  const { pitch, change, windowSec } = voice;
  const count = Math.min(pitch.length, change.length, envelope.rms.length);
  const loud = (i: number) => toDb(envelope.rms[i]!) >= levels.thresholdDb;
  // Colour is measured on short windows and jitters; the median of three follows the sound, not the noise.
  const colourChange = (i: number) => median3(change[i - 1] ?? change[i]!, change[i]!, change[i + 1] ?? change[i]!);
  const steady = new Uint8Array(count);
  let voiced = 0;
  for (let i = 1; i < count; i++) {
    const p = pitch[i]!;
    const previous = pitch[i - 1]!;
    if (p > 0 && loud(i)) voiced++;
    steady[i] = p > 0 && previous > 0 && loud(i) && Math.abs(Math.log(p / previous)) <= params.maxPitchStep && colourChange(i) <= params.maxChange ? 1 : 0;
  }
  // One odd window (a crackle, a pitch glitch) doesn't break a held sound.
  for (let i = 1; i < count - 1; i++) if (!steady[i] && steady[i - 1] && steady[i + 1]) steady[i] = 1;

  /**
   * Widens a held sound's core while neighbouring windows still sound like it:
   * voiced, near its pitch, colour barely moving. Limited, so a word next to it is never taken.
   */
  const growHeldSound = (first: number, end: number): [number, number] => {
    const reach = Math.round(GROW_SEC / windowSec);
    let low = Infinity;
    let high = 0;
    for (let k = first; k < end; k++) if (pitch[k]! > 0) [low, high] = [Math.min(low, pitch[k]!), Math.max(high, pitch[k]!)];
    const similar = (k: number) => {
      const p = pitch[k]!;
      return p > 0 && loud(k) && p >= low * (1 - GROW_PITCH) && p <= high * (1 + GROW_PITCH) && colourChange(k) <= params.maxChange * 2;
    };
    let from = first;
    while (from > 0 && first - from < reach && similar(from - 1)) from--;
    let to = end;
    while (to < count && to - end < reach && similar(to)) to++;
    return [from, to];
  };

  const found: TimeRange[] = [];
  let total = 0;
  let i = 0;
  while (i < count) {
    if (!steady[i]) {
      i++;
      continue;
    }
    // A steady run includes the window it is measured against.
    const first = Math.max(0, i - 1);
    let j = i;
    while (j < count && steady[j]) j++;
    const length = (j - first) * windowSec;
    if (length >= params.minSec && length <= params.maxSec && pitchSpan(pitch, first, j) <= params.maxPitchSpan) {
      // The strict test finds the core of the sound; its onset and fade wobble a little more.
      const [from, to] = growHeldSound(first, j);
      const start = from * windowSec;
      const end = to * windowSec;
      // Right after a word (no pause before), keep the word's own vowel.
      const afterWord = from > 0 && loud(from - 1);
      const cutStart = afterWord ? start + WORD_HEAD_SEC : start;
      const cutEnd = end - TAIL_SEC;
      if (cutEnd - cutStart >= MIN_CUT_SEC) {
        found.push(range(cutStart, cutEnd));
        total += cutEnd - cutStart;
      }
    }
    i = j;
  }

  const voicedSec = voiced * windowSec;
  const music = voicedSec >= MIN_VOICED_SEC_FOR_SHARE && total > voicedSec * MAX_FILLER_SHARE;
  return { fillers: normalizeRanges([...(music ? [] : found), ...fromWords]) };
}

const median3 = (a: number, b: number, c: number) => Math.max(Math.min(a, b), Math.min(Math.max(a, b), c));

function pitchSpan(pitch: Float32Array, from: number, to: number): number {
  let low = Infinity;
  let high = 0;
  for (let i = from; i < to; i++) {
    const p = pitch[i]!;
    if (p <= 0) continue;
    low = Math.min(low, p);
    high = Math.max(high, p);
  }
  return high > 0 ? Math.log(high / low) : Infinity;
}
